import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import {
  PrismIcon,
  PrismNumberInput,
  PrismRadioGroup,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import ExceptionIndicator from 'lumen/components/hotel/pricing-assistant/exception-indicator';
import {
  MIN_LOS_RESTRICTION_TYPE,
  NUMBER_INPUT_MAX_VALUE,
} from 'lumen/utils/constants/pricing-assistant-constants';

import type {
  RateGroupRow,
  RateGroupSectionRow,
} from '../rate-groups-accordion';

interface TdMinLosRecommendationSignature {
  Args: {
    row: RateGroupRow | RateGroupSectionRow;
  };
}

/* The input only carries the error border; the accordion renders the
   message text above the table. */
const TdMinLosRecommendation = <template>
  {{#unless @row.isSection}}
    <span data-test-id="rate-group-min-los">
      {{#if @row.state.isLoading}}
        <span
          class="loading-pulse block w-[80px]"
          data-test-id="rate-group-loading"
        ></span>
      {{else if @row.state.automationOn}}
        <span class="flex items-center gap-100">
          {{#if @row.state.showException}}
            <ExceptionIndicator />
            {{@row.state.formattedFailedValue}}
          {{else}}
            <PrismIcon
              @color="on-intelligence"
              @name="automation"
              @size="small"
            />
            {{@row.state.formattedRecommendation}}
          {{/if}}
        </span>
      {{else}}
        <span class="flex items-start gap-100">
          {{#if @row.state.showException}}
            <ExceptionIndicator />
          {{else}}
            <PrismIcon @color="disabled" @name="automation-off" @size="small" />
          {{/if}}
          <PrismRadioGroup
            @isSmall={{true}}
            @name="rate-group-{{@row.rateGroupId}}-type"
            @onChange={{@row.state.onTypeChange}}
            @value={{@row.state.restrictionType}}
            aria-label={{t "pricing_manager.restrictions.min_los_label"}}
            aria-labelledby="rate-group-{{@row.rateGroupId}}-name rate-group-{{@row.rateGroupId}}-type"
            class="gap-300"
            data-test-id="rate-group-min-los-type"
            id="rate-group-{{@row.rateGroupId}}-type"
            as |RadioGroup|
          >
            <RadioGroup.input
              @value={{MIN_LOS_RESTRICTION_TYPE.MIN_LOS}}
              class="items-center"
            >
              <span class="flex items-center gap-100">
                {{t "pricing_manager.restrictions.min_los_label"}}
                <span class="block w-[104px]">
                  <PrismNumberInput
                    @disabled={{eq
                      @row.state.restrictionType
                      MIN_LOS_RESTRICTION_TYPE.NO_RESTRICTION
                    }}
                    @hasErrors={{if @row.state.errorMessage true false}}
                    @inlineLabel={{t "pricing_manager.restrictions.nights"}}
                    @isSmall={{true}}
                    @isTouched={{@row.state.touched}}
                    @max={{NUMBER_INPUT_MAX_VALUE}}
                    @min={{0}}
                    @onValueChange={{@row.state.updateValue}}
                    @removePadding={{true}}
                    @required={{eq
                      @row.state.restrictionType
                      MIN_LOS_RESTRICTION_TYPE.MIN_LOS
                    }}
                    @value={{@row.state.value}}
                    aria-label={{t
                      "pricing_manager.restrictions.min_los_label"
                    }}
                    aria-labelledby="rate-group-{{@row.rateGroupId}}-name rate-group-{{@row.rateGroupId}}-min-los-input"
                    data-test-id="rate-group-min-los-input"
                    id="rate-group-{{@row.rateGroupId}}-min-los-input"
                    {{on "blur" @row.state.onBlur}}
                  />
                </span>
              </span>
            </RadioGroup.input>
            <RadioGroup.input
              @value={{MIN_LOS_RESTRICTION_TYPE.NO_RESTRICTION}}
            >
              {{t "pricing_manager.restrictions.no_min_los"}}
            </RadioGroup.input>
          </PrismRadioGroup>
        </span>
      {{/if}}
    </span>
  {{/unless}}
</template> satisfies TOC<TdMinLosRecommendationSignature>;

export default TdMinLosRecommendation;
